<template>
  <Toast />
  <vue3-snackbar top center success="#BD976E" :duration="2000"></vue3-snackbar>
  <Spinner :loading="orderdetails.loading" />
  <div v-if="!orderdetails.loading" class="od-page">

    <!-- ── HEADER ──────────────────────────────────────────────── -->
    <div class="od-header">
      <nav class="od-breadcrumb">
        <router-link to="/account/orders">All Orders</router-link>
        <ChevronRightIcon class="w-3 h-3 text-gray-300 flex-shrink-0" />
        <span>Order #{{ orderdetails.data.data?.order_number }}</span>
      </nav>

      <div class="od-header-row">
        <div class="od-header-left">
          <div class="flex flex-wrap items-center gap-3">
            <PopoverRoot v-if="ordersForSwitcher.length" v-model:open="isOrderSwitcherOpen">
              <PopoverTrigger class="od-title-trigger" aria-label="Switch order">
                <h1 class="od-title">{{ orderdetails.data.data?.order_number }}</h1>
                <ChevronDown class="w-4 h-4 od-title-chevron" />
              </PopoverTrigger>
              <PopoverContent align="start" :side-offset="8"
                class="rounded-lg bg-white border border-gray-200 shadow-lg p-2 outline-none"
                style="z-index: 1000; width: 22rem; max-width: calc(100vw - 2rem); max-height: calc(100vh - 8rem);">
                <div
                  class="relative flex items-center w-full h-8 border border-gray-200 rounded-lg bg-white text-gray-400 mb-2 focus-within:border-gray-400 focus-within:text-gray-500">
                  <Search class="absolute left-2.5 w-3.5 h-3.5 pointer-events-none flex-shrink-0 text-inherit" />
                  <input v-model="orderSwitcherSearch" type="search"
                    class="w-full h-full border-none bg-transparent pl-8 pr-2 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
                    placeholder="Search order #, location, status" @keydown.stop />
                </div>

                <div class="od-order-switcher-scroll max-h-80 overflow-auto space-y-1 pr-0.5"
                  style="scrollbar-width: thin; scrollbar-color: #9ca3af #f3f4f6;">
                  <button v-for="order in filteredOrdersForSwitcher" :key="order.id" type="button"
                    class="w-full text-left rounded-md border border-transparent px-2.5 py-2 transition-colors hover:bg-gray-50 hover:border-gray-200"
                    :class="{ 'border-gray-300 bg-gray-50': String(order.id) === String(currentOrderId) }"
                    @click="switchToOrder(order)">
                    <div class="text-xs font-semibold text-theme-secondary mb-1">{{ order.order_number || 'Order' }}</div>
                    <div class="flex items-center justify-between gap-2 text-xxs text-gray-500">
                      <span>{{ formatSwitcherDate(order.created_at) }}</span>
                      <span class="od-payment-badge paid">{{ order.status?.toUpperCase() }}</span>
                    </div>
                  </button>

                  <div v-if="!filteredOrdersForSwitcher.length"
                    class="px-2.5 py-3 text-xs text-gray-400">
                    No matching orders found.
                  </div>
                </div>
              </PopoverContent>
            </PopoverRoot>
            <h1 v-else class="od-title">{{ orderdetails.data.data?.order_number }}</h1>
            <span class="od-payment-badge" :class="orderdetails.data.data?.payment_status?.toLowerCase()">
              <span class="od-payment-badge__dot"></span>
              {{ orderdetails.data.data?.payment_status || 'Pending' }}
            </span>
          </div>
        </div>

        <div class="od-header-actions">
          <DropdownMenuRoot v-model:open="isDocMenuOpen">
            <DropdownMenuTrigger class="od-action-btn od-docs-trigger">
              <Icon name="document-file" class="w-3.5 h-3.5" />
              <span>Documents</span>
              <ChevronDown class="w-3.5 h-3.5 od-docs-chevron" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end" :side-offset="6" class="od-docs-menu" style="z-index: 1000;">
                <DropdownMenuItem v-for="(inv, index) in (orderdetails.data.data?.invoice || [])" :key="inv.id"
                  class="od-docs-item" @select="openInNewTab(`/invoice/${inv.id}`)">
                  <FileText class="w-3.5 h-3.5" />
                  <span>Invoice{{ (orderdetails.data.data?.invoice || []).length > 1 ? ` ${index + 1}` : '' }}</span>
                </DropdownMenuItem>

                <DropdownMenuItem v-if="orderdetails.data.shipments != '' && orderdetails.data.data?.trade_gecko_id"
                  class="od-docs-item" @select="openInNewTab(`/packing-list/${orderdetails.data.data.trade_gecko_id}`)">
                  <Package class="w-3.5 h-3.5" />
                  <span>Packing list</span>
                </DropdownMenuItem>

                <DropdownMenuItem class="od-docs-item"
                  @select="(e) => { e.preventDefault(); isDocMenuOpen = false; showExportModal = true; }">
                  <FileText class="w-3.5 h-3.5" />
                  <span>Export</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenuRoot>

          <!-- Reorder -->
          <button class="od-action-btn primary" @click="reOrder">
            <Icon name="refresh" class="w-3.5 h-3.5" />
            <span>Reorder</span>
          </button>
        </div>
      </div>
    </div>

    <InvoiceCsvExportPreview
      v-if="orderdetails.data.data"
      headless
      :open="showExportModal"
      @update:open="showExportModal = $event"
      :order-id="orderdetails.data.data.id"
      :order-number="orderdetails.data.data.order_number"
      :invoice-number="(orderdetails.data.data.invoice || [])[0]?.invoice_number || ''"
      :company-name="orderdetails.data.data.billing_address?.company || ''"
    />

    <OrderStatusInfoCard v-if="orderdetails.data.data" class="od-status-info-card" :order="orderdetails.data.data"
      :shipments="shipmentList" />

    <OrderLifecycleBar v-if="orderdetails.data.data" class="od-lifecycle-card" :order="orderdetails.data.data"
      :shipments="shipmentList" :show-update="true" />

    <OrderFulfillmentLayout v-if="orderdetails.data.data" :order="orderdetails.data.data" :shipments="shipmentList" />

    <template v-if="false">
      <!-- ── INFO CARDS ───────────────────────────────────────────── -->
      <Disclosure as="div" class="od-card sm:hidden" v-slot="{ open }">
        <DisclosureButton class="od-info-mobile-toggle">
          <span class="od-card-title od-info-mobile-title">Order Information</span>
          <ChevronRightIcon class="w-4 h-4 text-gray-400 transition-transform duration-200"
            :class="open ? 'rotate-90' : ''" />
        </DisclosureButton>
        <DisclosurePanel class="od-info-mobile-content">
          <section class="od-info-mobile-section">
            <dl class="od-dl">
              <div class="od-dl-row">
                <dt>Status</dt>
                <dd>
                  <span class="od-status sm" :class="orderdetails.data.data?.status?.toLowerCase()">
                    {{ orderdetails.data.data?.status }}
                  </span>
                </dd>
              </div>
              <div class="od-dl-row">
                <dt>Payment Status</dt>
                <dd>
                  <span class="od-payment-badge" :class="orderdetails.data.data?.payment_status?.toLowerCase()">
                    {{ orderdetails.data.data?.payment_status }}
                  </span>
                </dd>
              </div>
              <div class="od-dl-row" v-if="orderdetails.data.data?.payment_term">
                <dt>Payment Term</dt>
                <dd>{{ orderdetails.data.data?.payment_term }}</dd>
              </div>
              <div class="od-dl-row" v-if="primaryInvoiceNumber">
                <dt>Invoice Number</dt>
                <dd class="od-inline-copy">
                  <span class="font-semibold text-theme-secondary">{{ primaryInvoiceNumber }}</span>
                  <button class="od-copy-btn" @click="copyInvoiceNumber"
                    :aria-label="copiedInvoiceNumber ? 'Copied' : 'Copy invoice number'"
                    :title="copiedInvoiceNumber ? 'Copied' : 'Copy invoice number'">
                    <Check v-if="copiedInvoiceNumber" class="w-3.5 h-3.5" />
                    <Copy v-else class="w-3.5 h-3.5" />
                  </button>
                </dd>
              </div>
              <div class="od-dl-row" v-if="orderdetails.data.data?.reference_number">
                <dt>Reference</dt>
                <dd class="font-semibold text-theme-secondary">{{ orderdetails.data.data?.reference_number }}</dd>
              </div>
            </dl>
          </section>

          <section class="od-info-mobile-section">
            <h4 class="od-card-title">Shipping Address</h4>
            <address class="od-address">
              <p class="od-address-name">
                {{ orderdetails.data.data?.shipping_address?.nickname ||
                  orderdetails.data.data?.shipping_address?.company_name }}
              </p>
              <p class="od-address-line">{{ orderdetails.data.data?.shipping_address?.address1 }}</p>
              <p class="od-address-line" v-if="orderdetails.data.data?.shipping_address?.address2">
                {{ orderdetails.data.data?.shipping_address?.address2 }}
              </p>
              <p class="od-address-line">
                {{ orderdetails.data.data?.shipping_address?.city }},
                {{ orderdetails.data.data?.shipping_address?.state }}
                {{ orderdetails.data.data?.shipping_address?.zip_code }}
              </p>
            </address>
          </section>

          <section class="od-info-mobile-section">
            <h4 class="od-card-title">Billing Address</h4>
            <address class="od-address">
              <p class="od-address-name">
                {{ orderdetails.data.data?.billing_address?.nickname ||
                  orderdetails.data.data?.billing_address?.company_name }}
              </p>
              <p class="od-address-line">{{ orderdetails.data.data?.billing_address?.address1 }}</p>
              <p class="od-address-line" v-if="orderdetails.data.data?.billing_address?.address2">
                {{ orderdetails.data.data?.billing_address?.address2 }}
              </p>
              <p class="od-address-line">
                {{ orderdetails.data.data?.billing_address?.city }},
                {{ orderdetails.data.data?.billing_address?.state }}
                {{ orderdetails.data.data?.billing_address?.zip_code }}
              </p>
            </address>
          </section>
        </DisclosurePanel>
      </Disclosure>

      <div class="od-info-grid-desktop hidden sm:grid">
        <!-- Order Information -->
        <div class="od-card">
          <h4 class="od-card-title">Order Information</h4>
          <dl class="od-dl">
            <div class="od-dl-row">
              <dt>Status</dt>
              <dd>
                <span class="od-status sm" :class="orderdetails.data.data?.status?.toLowerCase()">
                  {{ orderdetails.data.data?.status }}
                </span>
              </dd>
            </div>
            <div class="od-dl-row">
              <dt>Payment Status</dt>
              <dd>
                <span class="od-payment-badge" :class="orderdetails.data.data?.payment_status?.toLowerCase()">
                  {{ orderdetails.data.data?.payment_status }}
                </span>
              </dd>
            </div>
            <div class="od-dl-row" v-if="orderdetails.data.data?.payment_term">
              <dt>Payment Term</dt>
              <dd>{{ orderdetails.data.data?.payment_term }}</dd>
            </div>
            <div class="od-dl-row" v-if="primaryInvoiceNumber">
              <dt>Invoice Number</dt>
              <dd class="od-inline-copy">
                <span class="font-semibold text-theme-secondary">{{ primaryInvoiceNumber }}</span>
                <button class="od-copy-btn" @click="copyInvoiceNumber"
                  :aria-label="copiedInvoiceNumber ? 'Copied' : 'Copy invoice number'"
                  :title="copiedInvoiceNumber ? 'Copied' : 'Copy invoice number'">
                  <Check v-if="copiedInvoiceNumber" class="w-3.5 h-3.5" />
                  <Copy v-else class="w-3.5 h-3.5" />
                </button>
              </dd>
            </div>
            <div class="od-dl-row" v-if="orderdetails.data.data?.reference_number">
              <dt>Reference</dt>
              <dd class="font-semibold text-theme-secondary">{{ orderdetails.data.data?.reference_number }}</dd>
            </div>
          </dl>
        </div>

        <!-- Shipping Address -->
        <div class="od-card">
          <h4 class="od-card-title">Shipping Address</h4>
          <address class="od-address">
            <p class="od-address-name">
              {{ orderdetails.data.data?.shipping_address?.nickname ||
                orderdetails.data.data?.shipping_address?.company_name }}
            </p>
            <p class="od-address-line">{{ orderdetails.data.data?.shipping_address?.address1 }}</p>
            <p class="od-address-line" v-if="orderdetails.data.data?.shipping_address?.address2">
              {{ orderdetails.data.data?.shipping_address?.address2 }}
            </p>
            <p class="od-address-line">
              {{ orderdetails.data.data?.shipping_address?.city }},
              {{ orderdetails.data.data?.shipping_address?.state }}
              {{ orderdetails.data.data?.shipping_address?.zip_code }}
            </p>
          </address>
        </div>

        <!-- Billing Address -->
        <div class="od-card">
          <h4 class="od-card-title">Billing Address</h4>
          <address class="od-address">
            <p class="od-address-name">
              {{ orderdetails.data.data?.billing_address?.nickname ||
                orderdetails.data.data?.billing_address?.company_name }}
            </p>
            <p class="od-address-line">{{ orderdetails.data.data?.billing_address?.address1 }}</p>
            <p class="od-address-line" v-if="orderdetails.data.data?.billing_address?.address2">
              {{ orderdetails.data.data?.billing_address?.address2 }}
            </p>
            <p class="od-address-line">
              {{ orderdetails.data.data?.billing_address?.city }},
              {{ orderdetails.data.data?.billing_address?.state }}
              {{ orderdetails.data.data?.billing_address?.zip_code }}
            </p>
          </address>
        </div>
      </div>

      <!-- ── SHIPMENT TRACKING + ORDER ACTIVITY ──────────────────── -->
      <div class="od-card od-card-flush">
        <!-- Tab bar -->
        <div class="od-tab-bar">
          <div class="flex">
            <button v-if="shipmentList.length" class="od-tab" :class="{ active: activeTrackingTab === 'shipments' }"
              @click="activeTrackingTab = 'shipments'">
              Shipments
              <span class="od-count-badge ml-1">{{ shipmentList.length }}</span>
            </button>
            <button class="od-tab" :class="{ active: activeTrackingTab === 'activity' || !shipmentList.length }"
              @click="activeTrackingTab = 'activity'">
              Order Activity
            </button>
          </div>
          <span v-if="shipmentList.length && activeTrackingTab === 'shipments'" class="text-xs text-gray-400 pr-4">
            {{ shipmentList.length }} Package{{ shipmentList.length > 1 ? 's' : '' }} Shipped
          </span>
        </div>

        <!-- Shipments panel -->
        <div v-show="shipmentList.length && activeTrackingTab === 'shipments'" class="px-4 pb-4 pt-3">
          <div class="od-tracking-wrap">
            <Disclosure v-for="shipment in shipmentList" :key="shipment.shipmentId" as="div" class="od-shipment"
              :defaultOpen="shipment.shipmentNo === 1" v-slot="{ open }">
              <DisclosureButton as="div" class="od-shipment-header">
                <div class="od-shipment-info-grid">
                  <div class="od-tracking-col compact">
                    <div class="od-shipment-meta">
                      <span class="od-shipment-number">#{{ shipment.shipmentNo }}</span>
                      <div class="od-carrier-wrap">
                        <img :src="getCarrierImage(shipment.carrier)" :alt="shipment.carrier" class="od-carrier-logo" />
                        <span class="od-col-value">{{ shipment.carrier }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="od-tracking-col compact">
                    <span class="od-col-label">Tracking Number</span>
                    <div class="od-tracking-value-wrap">
                      <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl" target="_blank" rel="noopener"
                        class="od-col-value accent od-inline-link" @click.stop>
                        {{ shipment.trackingNumber || 'Track Package' }}
                      </a>
                      <span v-else class="od-col-value">{{ shipment.trackingNumber || 'N/A' }}</span>

                      <button v-if="shipment.trackingNumber" class="od-copy-btn"
                        @click.stop="copyTrackingNumber(shipment)"
                        :aria-label="copiedShipmentId === shipment.shipmentId ? 'Copied' : 'Copy tracking number'"
                        :title="copiedShipmentId === shipment.shipmentId ? 'Copied' : 'Copy tracking number'">
                        <Check v-if="copiedShipmentId === shipment.shipmentId" class="w-3.5 h-3.5" />
                        <Copy v-else class="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div class="od-tracking-col compact">
                    <span class="od-col-label">Ship Date</span>
                    <span class="od-col-value">{{ shipment.shipDate ? moment(shipment.shipDate).format("MMM DD, YYYY") :
                      '—'
                    }}</span>
                  </div>

                  <div class="od-tracking-col compact">
                    <span class="od-col-label">Status</span>
                    <span class="od-status sm od-status-inline" :class="shipment.statusClass">
                      {{ shipment.status }}
                    </span>
                  </div>
                </div>

                <div class="od-shipment-toggle">
                  <span class="text-xs text-gray-400">{{ shipment.totalUnits }} units</span>
                  <ChevronRightIcon class="w-4 h-4 text-gray-400 transition-transform duration-200"
                    :class="open ? 'rotate-90' : ''" />
                </div>
              </DisclosureButton>

              <transition enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-y-95" enter-to-class="transform opacity-100 scale-y-100"
                leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-y-100"
                leave-to-class="transform opacity-0 scale-y-95">
                <DisclosurePanel class="od-shipment-body">
                  <div class="od-shipment-content-grid">
                    <div class="od-timeline-col">
                      <div class="flex items-center justify-between mb-2">
                        <div class="od-subtitle od-subtitle--no-mb">Timeline</div>
                        <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl" target="_blank" rel="noopener"
                          class="od-link-btn" @click.stop>
                          Track Package
                        </a>
                      </div>
                      <div class="od-timeline-list">
                        <div
                          v-for="(event, eventIdx) in (expandedTimelines[shipment.shipmentId] ? shipment.timeline : shipment.timeline.slice(0, 4))"
                          :key="eventIdx" class="od-timeline-item">
                          <span class="od-timeline-dot"></span>
                          <div class="od-timeline-content">
                            <p class="od-timeline-title">
                              {{ event.title }}
                              <span v-if="event.location" class="od-timeline-location">{{ event.location }}</span>
                            </p>
                            <span class="od-timeline-time">
                              {{ event.time ? moment(event.time).format("MMM DD, YYYY hh:mm A") : '—' }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <button v-if="shipment.timeline.length > 4" class="od-timeline-more-btn"
                        @click.stop="toggleTimeline(shipment.shipmentId)">
                        {{ expandedTimelines[shipment.shipmentId] ? 'View less' : `+${shipment.timeline.length - 4}
                        more`
                        }}
                      </button>

                      <div class="od-tracking-actions" v-if="shipment.document">
                        <button class="od-link-btn" @click="openInNewTab(shipment.document)">
                          {{ shipment.carrier.includes('Medusa') ? 'Proof of Pickup' : 'Download BOL' }}
                        </button>
                      </div>
                    </div>

                    <div class="od-products-col">
                      <div class="od-subtitle">Products in Shipment ({{ shipment.items.length }})</div>
                      <div class="od-shipment-products-scroll">
                        <div class="od-shipment-products">
                          <div v-for="(item, itemIdx) in shipment.items" :key="item.item_number || itemIdx"
                            class="od-shipment-product-row">
                            <div class="od-shipment-product-main">
                              <img :src="item.variant_image || item.product_image || '/images/no-image.png'"
                                :alt="item.variant_name || item.product_name" class="od-shipment-product-image" />
                              <div>
                                <p class="od-shipment-product-name">{{ item.product_name }}</p>
                                <p class="od-shipment-product-variant">{{ item.variant_name || item.name || 'Default' }}
                                </p>
                              </div>
                            </div>
                            <div class="od-shipment-product-qty">Qty: {{ parseInt(item.ship_qty || item.qty || 0) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </DisclosurePanel>
              </transition>
            </Disclosure>
          </div>
        </div>

        <!-- Activity panel -->
        <div v-show="!shipmentList.length || activeTrackingTab === 'activity'" class="od-activity-panel">
          <ul class="od-activity">
            <li class="od-activity-item">
              <span class="od-activity-dot" style="background:#6b7280"></span>
              <div>
                <p>Order Placed</p>
                <span>{{ moment(orderdetails.data.data?.created_at).format("MMM DD, YYYY h:mm A") }}</span>
              </div>
            </li>
            <template v-if="orderdetails.data.data?.payments?.length > 0">
              <li class="od-activity-item">
                <span class="od-activity-dot" style="background:#16a34a"></span>
                <div>
                  <p>Payment Verified</p>
                  <span>{{ moment(orderdetails.data.data.payments[0].updated_at ??
                    orderdetails.data.data.payments[0].created_at).format("DD MMM YYYY, h:mm A") }}</span>
                </div>
              </li>
            </template>
            <template v-if="shipmentList.length > 0">
              <li v-for="shipment in shipmentList" :key="shipment.shipmentId" class="od-activity-item">
                <span class="od-activity-dot" style="background:#3730a3"></span>
                <div>
                  <p>Shipment #{{ shipment.shipmentNo }} — {{ shipment.status }}</p>
                  <span class="block text-xs text-gray-400">{{ shipment.carrier }}</span>
                  <a v-if="shipment.trackingUrl" :href="shipment.trackingUrl" target="_blank" rel="noopener"
                    class="block text-xs text-indigo-500 hover:underline">
                    {{ shipment.trackingNumber || 'Track Package' }}
                  </a>
                  <span v-else-if="shipment.trackingNumber" class="block text-xs text-gray-500">{{
                    shipment.trackingNumber
                  }}</span>
                  <span>{{ shipment.shipDate ? moment(shipment.shipDate).format("MMM DD, YYYY") : '—' }}</span>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>

      <!-- ── MAIN: ITEMS + SUMMARY ────────────────────────────────── -->
      <div class="od-main">

        <div class="od-main-content">
          <div class="od-card od-card-flush">
            <div class="od-items-header">
              <div class="flex items-center gap-2">
                <h3 class="od-section-title">Order Items</h3>
                <span class="od-count-badge">{{ orderdetails.data.data?.order_line_items?.length }}</span>
              </div>
            </div>

            <!-- Items panel -->
            <div>
              <OrderItems :items="sortItems(orderdetails.data.data ? orderdetails.data.data.order_line_items : null)" />
            </div>

            <div>
              <div class="od-summary-compact">
                <!-- <div class="od-card-title px-4 pt-4">Billing Summary</div> -->
                <OrderSummary class="on-summary has-padding" :summary="ordersummary" />
              </div>
            </div>
          </div>

          <!-- Removed Products -->
          <div class="od-card od-card-flush" v-if="orderdetails.data.data?.cancel_line_items?.length > 0">
            <OrderItems :items="sortItems(orderdetails.data.data ? orderdetails.data.data.cancel_line_items : null)">
              <template #header>
                <div class="od-items-header">
                  <div class="flex items-center gap-2">
                    <Icon name="package-filled" class="w-4 h-4 text-gray-400" />
                    <h3 class="od-section-title">Products Removed from Order</h3>
                  </div>
                  <div v-tippy="{
                    content: 'Rarely, products that we thought were in stock or in perfect condition to ship are not. We have removed these products from the order and you will not be charged for them.',
                    placement: 'bottom-end',
                  }">
                    <Icon name="info" class="w-4 h-4 text-gray-400 cursor-pointer" />
                  </div>
                </div>
              </template>
            </OrderItems>
          </div>

        </div>
      </div>

    </template>

    <!-- ── REORDER CART (unchanged logic) ───────────────────────── -->
    <Cart :showCart="isReorderVisible" @close="isReorderVisible = false" :heading="heading" :isEmpty="!isDataLoading &&
      availableItems.length == 0 &&
      unavailableItems.length == 0
      " emptyDesc="You can go to order details to reorder again.">
      <div v-if="isDataLoading" class="h-full pt-8">
        <Spinner :loading="isDataLoading" />
      </div>
      <template v-else>
        <p class="text-sm font-light text-theme-body mb-3">
          You can reorder all products or adjust quantities
        </p>
        <div class="select-none" v-if="availableItems.length">
          <div class="cart-expanded -mt-5">
            <div class="cart-group-item" v-for="item in availableItems.sort((a, b) =>
              a.product_name.localeCompare(b.product_name)
            )" :key="item.id">
              <div class="cg-info">
                <img :src="item.image_url" />
                <div class="flex flex-col flex-grow sm:flex-grow-0 space-y-1.5">
                  <p>{{ item.product_name }}</p>
                  <span class="variant">{{ item.name }}</span>
                  <span class="price">${{ currencyFormat(item.price) }}</span>
                </div>
                <p class="sm:hidden">${{ currencyFormat(item.price * item.qty) }}</p>
              </div>
              <div class="cg-amount">
                <PlusMinusInput :id="item.id" v-model:modelValue="item.qty" :max="item.available_stock" :min="1" />
                <p class="hidden sm:inline-block">${{ currencyFormat(item.price * item.qty) }}</p>
                <button
                  class="opacity-100 md:opacity-100 group-hover:opacity-100 transition cursor-pointer bg-transparent focus:outline-none"
                  @click="removeFromItems(item)">
                  <Icon name="close" class="w-5 h-5 text-theme-red" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Disclosure v-slot="{ open }" as="div" class="select-none" :defaultOpen="true"
          v-if="unavailableItems.length > 0">
          <DisclosureButton as="div" class="cart-expander">
            <h3>Unavailable Products: {{ unavailableItems.length }}</h3>
            <div class="flex-grow flex items-center truncate">
              <svg width="600" height="1" viewBox="0 0 600 1" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="4.37114e-08" y1="0.5" x2="600" y2="0.500026" stroke="#EAEAEA" stroke-dasharray="11 11" />
              </svg>
            </div>
            <ChevronRightIcon class="flex-shrink-0 w-5 h-5 text-brand-body"
              :class="open ? 'transform rotate-90' : ''" />
          </DisclosureButton>
          <transition enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-y-95" enter-to-class="transform opacity-100 scale-y-100"
            leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-y-100"
            leave-to-class="transform opacity-0 scale-y-95">
            <DisclosurePanel class="cart-expanded -mt-5">
              <div class="cart-group-item opacity-60" v-for="item in unavailableItems.sort((a, b) =>
                a.unavailable - b.unavailable
              )" :key="item.id">
                <div class="cg-info">
                  <img :src="item.image_url" />
                  <div class="flex flex-col flex-grow sm:flex-grow-0 space-y-1.5">
                    <p>{{ item.product_name }}</p>
                    <span class="variant">{{ item.name }}</span>
                  </div>
                </div>
                <div class="cg-amount">
                  <span class="text-xs font-normal text-brand-body pr-2">
                    {{ item.unavailable ? 'Not Available' : 'Out of Stock' }}
                  </span>
                </div>
              </div>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </template>
      <template #footer>
        <div class="price-shipping-info mb-3" v-if="unavailableItems.length > 0">
          <button class="psi__close-btn">
            <Icon name="close" />
          </button>
          <p class="psi__text flex items-center space-x-0.5 mb-1.5">
            <Icon name="info-filled" class="w-3.5 h-3.5 mr-1.5 -mt-0.5 text-brand" />
            <span class="psi__text--bold">Some products are not available</span>
          </p>
          <p class="psi__text psi__text--xs">
            There are some products in your order that are no longer available and have to be removed before you can
            checkout.
          </p>
          <div
            class="flex items-center justify-start whitespace-nowrap mt-2 text-ellipsis overflow-hidden w-full select-none"
            v-if="unavailableItems.filter(i => i.unavailable == false).length > 0">
            <span>
              <input id="notify-inv" name="notify-inv" value="notify" v-model="notifyCheck" type="checkbox"
                class="h-3.5 w-3.5 border-2 mb-0.5 border-brand-gray border-opacity-50 rounded text-brand-secondary bg-transparent focus:ring-transparent" />
              <label for="notify-inv" class="text-xs font-normal text-theme-secondary whitespace-nowrap">
                Alert me when products are available.
              </label>
            </span>
          </div>
        </div>
        <div class="mt-6 grid gap-8" v-if="!isDataLoading"
          :class="{ 'grid-cols-2': availableItems.length || unavailableItems.length }">
          <Button title="Cancel" :outline="true" class="medium-large dark" :disabled="isProcessing"
            @click="isReorderVisible = false" />
          <Button v-if="availableItems.length || unavailableItems.length" :disabled="(availableItems.length || unavailableItems.filter(e => e.unavailable == false).length)
            ? false
            : true
            " :title="availableItems.length < 1
              ? 'Notify Me'
              : unavailableItems.length == 0
                ? 'Add all to Cart'
                : 'Add to Cart'
              " class="medium-large" @mouseup="addToCart" :loader="isProcessing" />
        </div>
      </template>
    </Cart>

  </div>
</template>

<script>
import Mymixin from "@/modules/mixins";
import { ref, inject, computed, watch, reactive } from "vue";
import Toast from "@/components/Toast.vue";
import OrderLifecycleBar from "./modules/OrderLifecycleBar.vue";
import OrderStatusInfoCard from "./modules/OrderStatusInfoCard.vue";
import OrderFulfillmentLayout from "./modules/OrderFulfillmentLayout.vue";
import OrderSummary from "./modules/OrderSummary.vue";
import OrderItems from "./modules/OrderItems.vue";
import { ChevronRightIcon } from "@heroicons/vue/solid";
import { useRoute, useRouter } from "vue-router";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/vue";
import { Copy, Check, ChevronDown, FileText, Package, Search } from "lucide-vue-next";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
} from "radix-vue";
import Account from "@/modules/Account";
import Cart from "@/components/Cart.vue";
import InvoiceCsvExportPreview from "@/components/InvoiceCsvExportPreview.vue";
import { useStore } from "vuex";

export default {
  name: "OrderDetails",
  mixins: [Mymixin],
  components: {
    Toast,
    OrderStatusInfoCard,
    OrderLifecycleBar,
    OrderFulfillmentLayout,
    OrderSummary,
    OrderItems,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    DropdownMenuRoot,
    DropdownMenuTrigger,
    DropdownMenuPortal,
    DropdownMenuContent,
    DropdownMenuItem,
    PopoverRoot,
    PopoverTrigger,
    PopoverContent,
    Cart,
    InvoiceCsvExportPreview,
    ChevronRightIcon,
    Copy,
    Check,
    ChevronDown,
    FileText,
    Package,
    Search,
  },
  methods: {
    sortItems(data) {
      return data
        ? data.sort((a, b) => a.productName.localeCompare(b.productName))
        : [];
    },

    getTotal(data) {
      let temp = 0;
      if (data)
        data.forEach((element) => (temp += element.qty * element.singlePrice));
      return temp;
    },

    trackingLink(data) {
      window.open(data);
    },
    moment: function (date) {
      return moment(date);
    },
    openInNewTab(url) {
      window.open(url, "_blank", "noreferrer");
    },
    downloadCSV(id, order_number) {
      this.downloadCSVLoading = true;
      let time = moment(new Date()).valueOf();
      let file_name = order_number + "_" + time + ".csv";

      this.axios.authApi
        .get("/order/csv/" + id, { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', file_name);
          document.body.appendChild(link);
          link.click();
          this.downloadCSVLoading = false;
        })
        .catch((error) => {
          this.downloadCSVLoading = false;
          console.log(error)
        })
        .finally(() => {
          this.downloadCSVLoading = false;
        });
    }
  },
  computed: {
    ordersummary() {
      const orders = {
        data: {
          // subTotal : (this.getTotal(this.orderdetails.data.data.order_line_items)).toFixed(2),
          subTotal: this.getTotal(
            this.orderdetails.data.data.order_line_items
          ).toFixed(2),
          taxTotal: this.orderdetails.data.data.taxes,
          shipping: this.orderdetails.data.data.shippingTotal,
          gTotal: Number(this.orderdetails.data.data.orderTotal).toFixed(
            2
          ),
          discount: this.orderdetails.data.data.discounts,
        },
      };
      return orders;
    },
    shippingId: {
      get() {
        return this.checkoutData.data.shipping_id;
      },
      set(newValue) {
        this.checkoutData.data.shipping_id = newValue;
      },
    },
    billingId: {
      get() {
        return this.checkoutData.data.billing_id;
      },
      set(newValue) {
        this.checkoutData.data.billing_id = newValue;
      },
    },
    shipTo: {
      get() {
        return this.addresses.data.find(
          (address) =>
            address.trade_gecko_id == this.checkoutData.data.shipping_id
        );
      },
    },
    billTo: {
      get() {
        return this.addresses.data.find(
          (address) =>
            address.trade_gecko_id == this.checkoutData.data.billing_id
        );
      },
    },
  },
  data() {
    return {};
  },
  setup() {
    const axios = inject("axios");
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const shipping_address = ref(null);
    const notifyCheck = ref(true);
    const isDocMenuOpen = ref(false);
    const showExportModal = ref(false);

    const activeTrackingTab = ref('shipments');
    const isReorderVisible = ref(false);
    const allAvailableProducts = ref([]);
    const allUnavailableProducts = ref([]);
    const isLoading = ref(true);
    const isProcessing = ref(false);
    const downloadCSVLoading = ref(false);
    const copiedShipmentId = ref(null);
    const copiedInvoiceNumber = ref(false);
    const isOrderSwitcherOpen = ref(false);
    const orderSwitcherSearch = ref("");
    const expandedTimelines = reactive({});
    const toggleTimeline = (shipmentId) => {
      expandedTimelines[shipmentId] = !expandedTimelines[shipmentId];
    };

    const heading = computed(() =>
      orderdetails.data.data
        ? "Reorder products from #" + orderdetails.data.data.order_number
        : null
    );

    const availableItems = computed(() => allAvailableProducts.value);

    const unavailableItems = computed(() => allUnavailableProducts.value);

    const isDataLoading = computed(() => isLoading.value);

    //Get Order Details
    const {
      orderdetails,
      getOrderdetails,
      reOrderdetails,
      getReOrderDetails,
    } = Account();

    watch(
      () => route.params.id,
      () => {
        getOrderdetails();
      },
      { immediate: true }
    );

    const getCarrierImage = (shipMethod = "") => {
      let image = "/images/no-image.png";
      if (shipMethod.includes("FedEx")) {
        image = "/images/fedex.png";
      } else if (shipMethod.includes("UPS")) {
        image = "/images/ups.png";
      } else if (shipMethod.includes("DHL")) {
        image = "/images/DHL.png";
      } else if (shipMethod.includes("USPS")) {
        image = "/images/usps.png";
      } else if (shipMethod.includes("Medusa")) {
        image = "/images/medusa.png";
      }
      return image;
    };

    const getLatestShipmentEvent = (shipmentItem = {}) => {
      const timelineCandidates = [
        shipmentItem.history,
        shipmentItem.timeline,
        shipmentItem.tracking_history,
        shipmentItem.tracking_timeline,
        shipmentItem.events,
        shipmentItem.tracking_events,
        shipmentItem.tracking?.events,
        shipmentItem.tracking_data?.events,
        shipmentItem.tracking_data?.timeline,
      ];

      const timeline = timelineCandidates.find((candidate) =>
        Array.isArray(candidate) && candidate.length
      );

      return timeline?.[0] || null;
    };

    const getShipmentStatusLabel = (shipmentItem = {}) => {
      const apiStatus =
        shipmentItem.status ||
        shipmentItem.shipment_status ||
        shipmentItem.tracking_status;

      if (apiStatus) {
        return apiStatus;
      }

      const latestEvent = getLatestShipmentEvent(shipmentItem);
      const latestStatus =
        latestEvent?.status ||
        latestEvent?.title ||
        latestEvent?.label ||
        latestEvent?.event;

      if (latestStatus) {
        return latestStatus;
      }

      return shipmentItem.ship_via?.includes("LTL") ||
        shipmentItem.ship_via?.includes("Medusa")
        ? "Picked Up"
        : "Shipped";
    };

    const getShipmentStatusClass = (shipmentItem = {}) => {
      const status = getShipmentStatusLabel(shipmentItem).toLowerCase();

      if (status.includes("deliver")) return "delivered";
      if (status.includes("cancel")) return "cancelled";
      if (status.includes("void")) return "void";
      if (status.includes("process")) return "processing";
      if (status.includes("pick")) return "open";
      if (status.includes("transit") || status.includes("vehicle") || status.includes("way")) return "in_transit";
      if (status.includes("ship") || status.includes("dispatch")) return "shipped";

      return "open";
    };

    const getFallbackLocation = () => {
      const shipping = orderdetails.data.data?.shipping_address;
      if (!shipping) return "";

      return [shipping.city, shipping.state_code || shipping.state, shipping.zip_code]
        .filter(Boolean)
        .join(" ");
    };

    const getShipmentTimeline = (shipmentItem = {}) => {
      const timelineCandidates = [
        shipmentItem.history,
        shipmentItem.timeline,
        shipmentItem.tracking_history,
        shipmentItem.tracking_timeline,
        shipmentItem.events,
        shipmentItem.tracking_events,
        shipmentItem.tracking?.events,
        shipmentItem.tracking_data?.events,
        shipmentItem.tracking_data?.timeline,
      ];

      const timeline = timelineCandidates.find((candidate) =>
        Array.isArray(candidate)
      );

      if (timeline && timeline.length) {
        return timeline
          .map((event) => ({
            title:
              event.status ||
              event.description ||
              event.event ||
              event.title ||
              event.activity ||
              "Update",
            location:
              event.location ||
              [event.city, event.state, event.zip_code || event.postal_code]
                .filter(Boolean)
                .join(" "),
            time:
              event.time ||
              event.date ||
              event.datetime ||
              event.event_time ||
              event.timestamp ||
              event.created_at ||
              event.updated_at,
          }))
          .filter((event) => event.title || event.location || event.time);
      }

      const isPickup = /pick.?up.?at.?medusa|medusa.?pick.?up/i.test(String(shipmentItem.ship_via || ''));
      return [
        {
          title: isPickup ? "Confirmed Released" : "Shipment created",
          location: "Roseland NJ 07068",
          time: shipmentItem.ship_date || shipmentItem.created_at || null,
        },
      ];
    };

    const cleanCarrierName = (name) =>
      String(name || "").replace(/\s*\(Perseuss\)\s*/gi, "").trim();

    const shipmentList = computed(() => {
      const shipments = orderdetails.data?.shipments;
      if (!shipments || shipments === "") return [];

      if (Array.isArray(shipments)) {
        return shipments.map((shipment, idx) => ({
          shipmentNo: idx + 1,
          shipmentId: shipment.shipment_id || shipment.id || idx,
          carrier: cleanCarrierName(shipment.ship_via || shipment.carrier || shipment.name || "Carrier"),
          trackingNumber: shipment.tracking_number || shipment.trackingNumber || "",
          trackingUrl: shipment.tracking_url || shipment.trackingUrl || "",
          shipDate: shipment.ship_date || shipment.shipDate || shipment.created_at || null,
          estimatedDeliveryDate: shipment.estimated_delivery_date || shipment.estimatedDeliveryDate || shipment.eta || null,
          deliveredDate: shipment.delivered_at || shipment.deliveredAt || shipment.delivery_date || null,
          status: getShipmentStatusLabel(shipment),
          statusClass: getShipmentStatusClass(shipment),
          document: shipment.document || null,
          timeline: getShipmentTimeline(shipment),
          items: shipment.items || [],
          totalUnits: Number(shipment.ship_qty || 0),
          carrierName: shipment.carrier_name || "",
          carrierImageUrl: shipment.carrier_image_url || "",
          carrierBaseUrl: shipment.carrier_base_url || "",
        }));
      }

      return Object.entries(shipments).map(([shipmentId, shipmentData], idx) => {
        const isObj = typeof shipmentData === 'object' && !Array.isArray(shipmentData) && shipmentData !== null;
        const sh = isObj ? shipmentData : {};
        const shipmentItems = isObj
          ? (Array.isArray(sh.items) ? sh.items : [])
          : (Array.isArray(shipmentData) ? shipmentData : []);
        const firstItem = !isObj ? (shipmentItems[0] || {}) : sh;

        const totalUnits = shipmentItems.reduce(
          (total, item) => total + Number(item.ship_qty || item.qty || 0),
          0
        );

        return {
          shipmentNo: idx + 1,
          shipmentId: sh.shipment_id || shipmentId,
          carrier: cleanCarrierName(sh.ship_via || firstItem.ship_via || firstItem.carrier || "Carrier"),
          trackingNumber: sh.tracking_number || firstItem.tracking_number || firstItem.trackingNumber || "",
          trackingUrl: sh.tracking_url || firstItem.tracking_url || firstItem.trackingUrl || "",
          shipDate: sh.ship_date || firstItem.ship_date || firstItem.shipDate || null,
          estimatedDeliveryDate: sh.estimated_delivery_date || firstItem.estimated_delivery_date || sh.estimatedDeliveryDate || firstItem.estimatedDeliveryDate || sh.eta || firstItem.eta || null,
          deliveredDate: sh.delivered_at || firstItem.delivered_at || sh.deliveredAt || firstItem.deliveredAt || sh.delivery_date || firstItem.delivery_date || null,
          status: getShipmentStatusLabel(firstItem),
          statusClass: getShipmentStatusClass(firstItem),
          document: sh.document || firstItem.document || null,
          timeline: getShipmentTimeline(sh.tracking_history ? sh : firstItem),
          items: shipmentItems,
          totalUnits,
          carton: sh.carton || null,
          proNumber: sh.pro_number || null,
          pickedUpBy: sh.picked_up_by || null,
          readyOn: sh.ready_on || sh.ship_date || null,
          carrierName: sh.carrier_name || firstItem.carrier_name || "",
          carrierImageUrl: sh.carrier_image_url || firstItem.carrier_image_url || "",
          carrierBaseUrl: sh.carrier_base_url || firstItem.carrier_base_url || "",
        };
      });
    });

    const primaryInvoiceNumber = computed(() => {
      const invoices = orderdetails.data?.data?.invoice;
      if (!Array.isArray(invoices) || !invoices.length) return "";
      return invoices[0]?.invoice_number || "N/A";
    });

    const currentOrderId = computed(() => route.params.id);

    const ordersForSwitcher = computed(() => orderdetails.data?.switchable_orders || []);

    const formatSwitcherCurrency = (value) => {
      const amount = Number(value || 0);
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const formatSwitcherDate = (date) => {
      if (!date) return "";
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return "";
      return parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const filteredOrdersForSwitcher = computed(() => {
      const query = orderSwitcherSearch.value.trim().toLowerCase();
      const list = ordersForSwitcher.value;
      if (!query) return list;
      return list.filter((order) =>
        String(order?.order_number || "").toLowerCase().includes(query)
      );
    });

    const switchToOrder = (order) => {
      const nextOrderId = order?.id;
      if (!nextOrderId) return;

      isOrderSwitcherOpen.value = false;

      if (String(nextOrderId) === String(route.params.id)) return;
      router.push(`/order-details/${nextOrderId}`);
    };

    const copyText = async (text) => {
      if (!text) return;

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const input = document.createElement("textarea");
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
    };

    const copyTrackingNumber = async (shipment) => {
      if (!shipment?.trackingNumber) return;

      try {
        await copyText(shipment.trackingNumber);

        copiedShipmentId.value = shipment.shipmentId;
        setTimeout(() => {
          if (copiedShipmentId.value === shipment.shipmentId) {
            copiedShipmentId.value = null;
          }
        }, 1200);
      } catch (error) {
        console.log(error);
      }
    };

    const copyInvoiceNumber = async () => {
      if (!primaryInvoiceNumber.value) return;

      try {
        await copyText(primaryInvoiceNumber.value);
        copiedInvoiceNumber.value = true;
        setTimeout(() => {
          copiedInvoiceNumber.value = false;
        }, 1200);
      } catch (error) {
        console.log(error);
      }
    };

    watch(reOrderdetails, (details) => {
      isLoading.value = details.loading;
      if (details.data.data) {
        const { availble_products, unavailble_products } = details.data.data;
        allAvailableProducts.value = availble_products || [];
        allUnavailableProducts.value = unavailble_products || [];
      }
    });

    function reOrder() {
      getReOrderDetails();
      isReorderVisible.value = true;
    }

    const removeFromItems = (prod) => {
      const pidx = allAvailableProducts.value.findIndex(
        (p) => p.sku === prod.sku
      );
      allAvailableProducts.value.splice(pidx, 1);
    };

    function addToCart() {
      const items = availableItems.value.map((i) => ({
        variant_id: i.id,
        qty: i.qty,
      }));
      let notify = [];

      if (notifyCheck.value) {
        notify = unavailableItems.value.filter(i => i.unavailable == false).map(j => j.id)
      }

      if (items.length > 0 || notify > 0) {
        isProcessing.value = true;
        axios.authApi
          .post("/add-products-to-cart", { items, notify })
          .then((resp) => {
            if (resp.status) {
              isReorderVisible.value = false;

              // Pull Cart Items
              store.dispatch("getCart", { ax: axios });
            } else {
              console.log(resp.error);
            }
          })
          .catch(function (error) {
            console.log(error);
          })
          .finally(() => (isProcessing.value = false));
      }
    }

    return {
      orderdetails,
      shipping_address,
      isReorderVisible,
      notifyCheck,
      availableItems,
      unavailableItems,
      reOrderdetails,
      getReOrderDetails,
      reOrder,
      addToCart,
      heading,
      axios,
      removeFromItems,
      isDataLoading,
      isProcessing,
      downloadCSVLoading,
      shipmentList,
      primaryInvoiceNumber,
      getCarrierImage,
      copiedShipmentId,
      copiedInvoiceNumber,
      copyTrackingNumber,
      copyInvoiceNumber,
      isOrderSwitcherOpen,
      orderSwitcherSearch,
      currentOrderId,
      ordersForSwitcher,
      filteredOrdersForSwitcher,
      formatSwitcherCurrency,
      formatSwitcherDate,
      switchToOrder,
      activeTrackingTab,
      expandedTimelines,
      toggleTimeline,
      isDocMenuOpen,
      showExportModal,
    };
  },
};
</script>

<style lang="scss" scoped>
// ─── PAGE CONTAINER ──────────────────────────────────────────────
.od-page {
  @apply flex flex-col gap-5 w-full pb-12;
  @include maxContainer();
}

// ─── HEADER ──────────────────────────────────────────────────────
.od-header {
  @apply flex flex-col gap-2;
}

.od-header-row {
  @apply flex flex-wrap items-center justify-between gap-3;
}

.od-header-left {
  @apply flex flex-col gap-0.5;
}

.od-breadcrumb {
  @apply flex items-center gap-1.5 text-xs text-gray-400;

  a {
    @apply hover:text-theme-secondary transition-colors;
  }

  span {
    @apply text-theme-body;
  }
}

.od-title {
  @apply text-2xl font-bold text-theme-secondary leading-none;
}

.od-title-trigger {
  @apply inline-flex items-center gap-1.5 cursor-pointer rounded-md px-1.5 py-1 -mx-1.5 -my-1 transition-colors;

  &:hover {
    background: rgba(17, 24, 39, 0.04);
  }
}

.od-title-chevron {
  @apply text-gray-400 transition-transform duration-150;
}

.od-title-trigger[data-state="open"] .od-title-chevron {
  transform: rotate(180deg);
}

.od-header-kpi {
  @apply inline-flex items-center text-xxs font-semibold text-theme-secondary bg-gray-100 rounded-full px-2.5 py-1;
}

.od-placed-on {
  @apply text-xs text-gray-400 mt-0.5;
}

.od-header-actions {
  @apply flex flex-wrap items-center gap-2 flex-shrink-0;
}

.od-action-btn {
  @apply inline-flex items-center gap-1.5 text-xs font-medium text-theme-secondary border border-gray-300 bg-white hover:bg-gray-100 px-3 py-2 rounded-md leading-none transition-colors duration-150 whitespace-nowrap;

  &.primary {
    @apply bg-theme-secondary text-white border-theme-secondary hover:bg-opacity-90;
  }
}

.od-docs-trigger {
  @apply gap-1.5;
}

.od-docs-chevron {
  @apply text-gray-400 transition-transform duration-150;
}

.od-docs-trigger[data-state="open"] .od-docs-chevron {
  transform: rotate(180deg);
}

.od-docs-menu {
  min-width: 12.5rem;
  border-radius: 0.375rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0.25rem;
  outline: none;
}

.od-docs-item {
  @apply flex items-center gap-2 px-2.5 py-2 text-xs text-theme-secondary rounded cursor-pointer select-none outline-none;
}

.od-docs-item[data-highlighted] {
  @apply bg-gray-100;
}

// ─── STATUS BADGE ────────────────────────────────────────────────
.od-status {
  @apply inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium;

  &.sm {
    @apply text-xs py-1;
  }

  &.open {
    color: #1d6fa8;
    background: rgba(29, 111, 168, 0.1);
  }

  &.shipped {
    color: #5b21b6;
    background: rgba(91, 33, 182, 0.1);
  }

  &.shipping {
    color: #3730a3;
    background: rgba(55, 48, 163, 0.1);
  }

  &.delivered {
    color: #166534;
    background: rgba(22, 101, 52, 0.1);
  }

  &.finalized {
    color: #065f46;
    background: rgba(6, 95, 70, 0.1);
  }

  &.cancelled {
    color: #991b1b;
    background: rgba(153, 27, 27, 0.1);
  }

  &.void {
    color: #92400e;
    background: rgba(146, 64, 14, 0.1);
  }

  &.pickup {
    color: #1d6fa8;
    background: rgba(29, 111, 168, 0.1);
  }

  &.processing {
    color: #3730a3;
    background: rgba(55, 48, 163, 0.1);
  }
}

.od-payment-badge {
  @apply inline-flex items-center px-2 py-0.5 rounded-full text-xxs font-semibold;
  color: #3f6484;
  background: rgba(121, 185, 224, 0.16);
  text-transform: capitalize;
  gap: 0.375rem;

  .od-payment-badge__dot {
    @apply w-1.5 h-1.5 rounded-full flex-shrink-0;
    background: currentColor;
  }

  &.paid,
  &.approved {
    color: #166534;
    background: rgba(22, 101, 52, 0.1);
  }

  &.unpaid,
  &.overdue {
    color: #991b1b;
    background: rgba(153, 27, 27, 0.1);
  }

  &.pending {
    color: #92400e;
    background: rgba(146, 64, 14, 0.1);
  }

  &.partial {
    color: #9a6700;
    background: rgba(245, 158, 11, 0.14);
  }
}

// ─── INFO GRID (3-col cards) ─────────────────────────────────────
.od-info-grid {
  @apply grid grid-cols-1 md:grid-cols-3 gap-4;
}

.od-info-grid-desktop {
  @apply grid-cols-2 lg:grid-cols-3 gap-4;
}

// ─── CARD ────────────────────────────────────────────────────────
.od-card {
  @apply bg-white rounded-lg p-4;
  border: 1px solid rgba(0, 0, 0, 0.07);

  &.od-card-flush {
    @apply p-0 overflow-hidden;
  }
}

.od-card-title {
  @apply text-xxs font-semibold uppercase tracking-widest text-gray-400 mb-3;
}

.od-info-mobile-toggle {
  @apply w-full flex items-center justify-between;
}

.od-info-mobile-title {
  @apply mb-0;
}

.od-info-mobile-content {
  @apply pt-3;
}

.od-info-mobile-section {
  @apply pb-4 mb-4 border-b border-gray-100;

  &:last-child {
    @apply mb-0 pb-0 border-b-0;
  }
}

// ─── DEFINITION LIST ─────────────────────────────────────────────
.od-dl {
  @apply flex flex-col gap-2.5;
}

.od-dl-row {
  @apply flex items-center justify-between gap-3;

  dt {
    @apply text-xs text-gray-400 flex-shrink-0;
  }

  dd {
    @apply text-xs text-theme-body text-right;
  }
}

.od-inline-copy {
  @apply inline-flex items-center justify-end gap-1.5;
}

// ─── ADDRESS ─────────────────────────────────────────────────────
.od-address {
  @apply not-italic;
}

.od-address-name {
  @apply text-sm font-semibold text-theme-secondary mb-1;
}

.od-address-line {
  @apply block text-xs text-theme-body leading-relaxed;
}

// ─── SECTION HEADER ──────────────────────────────────────────────
.od-section {
  @apply flex flex-col gap-3;
}

.od-section-header {
  @apply flex items-center justify-between;
}

.od-section-title {
  @apply text-sm font-semibold text-theme-secondary leading-none;
}

.od-count-badge {
  @apply inline-flex items-center justify-center bg-gray-100 text-gray-500 text-xxs font-semibold px-1.5 py-0.5 rounded-full;
  min-width: 1.25rem;
}

// ─── EMPTY STATE ─────────────────────────────────────────────────
.od-empty {
  @apply flex flex-col items-center justify-center gap-2 py-12 bg-white rounded-lg text-center;
  border: 1px solid rgba(0, 0, 0, 0.07);

  p {
    @apply text-xs text-gray-400 max-w-xs;
  }
}

// ─── TRACKING ────────────────────────────────────────────────────
.od-tracking-wrap {
  @apply flex flex-col gap-3;
}

.od-shipment {
  @apply bg-white rounded-lg overflow-hidden;
  border: 1px solid rgba(0, 0, 0, 0.07);
}

.od-shipment-header {
  @apply px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 cursor-pointer;
}

.od-shipment-info-grid {
  @apply grid grid-cols-2 lg:grid-cols-4 gap-3 flex-grow;
}

.od-tracking-col {
  @apply flex flex-col gap-1;

  &.compact {
    @apply min-w-0;
  }
}

.od-col-label {
  @apply text-xxs font-medium uppercase tracking-wide text-gray-400;
}

.od-col-value {
  @apply text-xs font-medium text-theme-body;

  &.accent {
    @apply font-semibold text-theme-secondary;
  }
}

.od-inline-link {
  @apply truncate border-b border-dashed border-theme-secondary hover:opacity-75 transition-opacity;
}

.od-shipment-meta {
  @apply flex items-center gap-2 min-w-0;
}

.od-shipment-number {
  @apply inline-flex items-center justify-center text-xxs font-semibold text-theme-secondary bg-gray-100 rounded px-2 py-1 leading-none;
}

.od-carrier-wrap {
  @apply flex items-center gap-2 min-w-0;
}

.od-carrier-logo {
  @apply w-8 h-5 object-contain flex-shrink-0;
}

.od-tracking-value-wrap {
  @apply flex items-center gap-2 min-w-0;
}

.od-copy-btn {
  @apply inline-flex items-center justify-center text-gray-400 rounded w-5 h-5 leading-none hover:text-theme-secondary hover:bg-gray-100 transition-colors;
}

.od-shipment-toggle {
  @apply flex items-center justify-between gap-2 lg:ml-4;
}

.od-shipment-body {
  @apply border-t border-gray-100 px-4 py-3;
}

.od-shipment-content-grid {
  @apply grid grid-cols-1 xl:grid-cols-2 gap-4;
}

.od-subtitle {
  @apply text-xxs font-semibold uppercase tracking-widest text-gray-400 mb-2;

  &--no-mb {
    @apply mb-0;
  }
}

.od-shipment-products-scroll {
  max-height: calc(5 * 53px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-200 rounded-full;
  }
}

.od-timeline-more-btn {
  @apply mt-2 text-xxs font-medium text-theme-secondary hover:opacity-70 transition-opacity leading-none;
}

.od-timeline-col,
.od-products-col {
  @apply min-w-0;
}

.od-timeline-col {
  @apply bg-gray-50 border border-gray-100 rounded-md p-3;
}

.od-timeline-list {
  @apply flex flex-col;
}

.od-timeline-item {
  @apply relative pl-5 pb-4;

  &:last-child {
    @apply pb-0;
  }

  &:not(:last-child)::before {
    content: "";
    @apply absolute top-2 bottom-0 w-px bg-gray-200;
    left: 6px;
  }
}

.od-timeline-dot {
  @apply absolute left-0 top-1 w-3 h-3 rounded-full bg-white border-2 border-theme-secondary;
}

.od-timeline-content {
  @apply min-w-0;
}

.od-timeline-title {
  @apply text-xs font-semibold text-theme-secondary leading-tight;
}

.od-timeline-location {
  @apply ml-1.5 text-xxs uppercase tracking-wide text-gray-400;
}

.od-timeline-time {
  @apply block text-xxs text-gray-500 mt-1;
}

.od-status-inline {
  width: fit-content;
}

.od-tracking-actions {
  @apply flex flex-wrap items-center gap-3 mt-3;
}

.od-link-btn {
  @apply text-xs text-theme-secondary border-b border-dashed border-theme-secondary leading-none hover:opacity-70 transition-opacity;
}

.od-shipment-products {
  @apply flex flex-col;
}

.od-shipment-product-row {
  @apply flex items-center justify-between gap-3 py-2 border-b border-gray-100;

  &:last-child {
    @apply border-b-0;
  }
}

.od-shipment-product-main {
  @apply flex items-center gap-2 min-w-0;
}

.od-shipment-product-image {
  @apply w-9 h-9 rounded object-cover bg-gray-50 flex-shrink-0;
}

.od-shipment-product-name {
  @apply text-xs font-semibold text-theme-secondary truncate;
}

.od-shipment-product-variant {
  @apply text-xxs text-gray-500 truncate;
}

.od-shipment-product-qty {
  @apply text-xs font-medium text-theme-body whitespace-nowrap;
}

// ─── MAIN LAYOUT ─────────────────────────────────────────────────
.od-main {
  @apply flex flex-col lg:flex-row items-start gap-5;
}

.od-main-content {
  @apply flex flex-col gap-4 flex-grow min-w-0 w-full;
}

.od-summary-compact {
  @apply w-full lg:max-w-sm lg:ml-auto border-t border-gray-100;
}

.od-sidebar {
  @apply flex flex-col gap-4 w-full lg:w-72 flex-shrink-0;
}

// ─── ORDER ITEMS HEADER ──────────────────────────────────────────
.od-items-header {
  @apply flex items-center justify-between px-4 py-3 border-b border-gray-100;
}

// ─── TABS ────────────────────────────────────────────────────────
.od-tab-bar {
  @apply flex items-center justify-between border-b border-gray-100;
}

.od-tab {
  @apply inline-flex items-center px-4 py-3 text-xs font-medium text-gray-400 border-b-2 border-transparent -mb-px transition-colors duration-150 cursor-pointer;

  &:hover {
    @apply text-theme-secondary;
  }

  &.active {
    @apply text-theme-secondary border-theme-secondary;
  }
}

.od-activity-panel {
  @apply px-4 py-4;
}

// ─── ORDER ACTIVITY ──────────────────────────────────────────────
.od-activity {
  @apply flex flex-col;
}

.od-activity-item {
  @apply relative flex items-start gap-3 pb-4;

  &:last-child {
    @apply pb-0;
  }

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 3px;
    top: 13px;
    bottom: 0;
    width: 1px;
    background: #e5e7eb;
  }

  .od-activity-dot {
    @apply w-2 h-2 rounded-full flex-shrink-0 mt-1 relative;
    z-index: 1;
  }

  p {
    @apply text-xs font-medium text-theme-secondary leading-none mb-1;
  }

  span {
    @apply text-xxs text-gray-400;
  }
}

// ─── EXPORT BUTTON (in tab bar) ──────────────────────────────────
:deep(.od-export-btn) {
  @apply flex-shrink-0 h-7 inline-flex text-xs items-center gap-1.5 bg-transparent text-theme-secondary border border-gray-200 hover:bg-theme-secondary hover:text-white px-3 rounded leading-none transition-colors duration-200 cursor-pointer;
}
</style>

<style lang="scss">
.od-docs-menu {
  min-width: 10rem;
  border-radius: 0.375rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0.25rem;
  outline: none;
}

.od-docs-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.75rem;
  color: #2b303f;
  border-radius: 0.25rem;
  cursor: pointer;
  user-select: none;
  outline: none;
}

.od-docs-item[data-highlighted] {
  background: #f3f4f6;
}

.od-order-switcher-scroll::-webkit-scrollbar {
  width: 8px;
}

.od-order-switcher-scroll::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 9999px;
}

.od-order-switcher-scroll::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 9999px;
  border: 2px solid #f3f4f6;
}

.od-order-switcher-scroll::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
</style>
